/**
 * Step 5 — Damage Photos per Area
 * Reuses existing /api/photos endpoints. Tracks per-area count & description acceptance.
 */
import { useState, useEffect, useCallback, useRef } from "react";
import API from "@/lib/api";
import { openCamera, openPhotoPicker } from "@/lib/camera";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Camera, ImageIcon, Loader2, Check, Edit2, AlertCircle } from "lucide-react";
import { toast } from "sonner";

const MIN_PER_AREA = 5;

export default function Step5Photos({ workflow, requestId, onAdvance }) {
  const areas = workflow?.step_4?.affected_areas || [];
  const [photos, setPhotos] = useState([]);
  const [selectedArea, setSelectedArea] = useState(areas[0] || "");
  const [uploading, setUploading] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editText, setEditText] = useState("");
  const [saving, setSaving] = useState(false);
  const fileRef = useRef(null);
  const camRef = useRef(null);

  const fetchPhotos = useCallback(async () => {
    try {
      const { data } = await API.get(`/photos/job/${requestId}`);
      setPhotos(Array.isArray(data?.photos) ? data.photos : []);
    } catch (e) { console.error(e); }
  }, [requestId]);

  useEffect(() => { fetchPhotos(); }, [fetchPhotos]);

  const countByArea = (area) => photos.filter(p => (p.area || "Other") === area).length;
  const acceptedByArea = (area) => photos.filter(p => (p.area || "Other") === area && p.is_approved).length;

  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!selectedArea) return toast.error("Pick an area first");
    setUploading(true);
    const fd = new FormData();
    fd.append("file", file);
    fd.append("request_id", requestId);
    fd.append("area", selectedArea);
    try {
      const { data } = await API.post("/photos/analyze", fd, { headers: { "Content-Type": "multipart/form-data" }, timeout: 60000 });
      toast.success(data?.queued ? "Saved offline. The photo will upload when you're back online." : "Photo uploaded");
      fetchPhotos();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
      if (camRef.current) camRef.current.value = "";
    }
  };

  const startEdit = (p) => {
    setEditingId(p.id);
    setEditText(p.description || p.ai_description || "");
  };
  const saveEdit = async (p) => {
    try {
      await API.post(`/photos/${p.id}/override`, { description: editText });
      toast.success("Description updated");
      setEditingId(null);
      fetchPhotos();
    } catch (e) { toast.error("Could not save"); }
  };
  const accept = async (p) => {
    try {
      await API.post(`/photos/${p.id}/approve`);
      fetchPhotos();
    } catch (e) { toast.error("Could not accept"); }
  };

  const allMin = areas.every(a => countByArea(a) >= MIN_PER_AREA);
  const allAccepted = photos.length > 0 && photos.every(p => p.is_approved);
  const canContinue = allMin && allAccepted;

  const continueStep = async () => {
    setSaving(true);
    try {
      const counts = Object.fromEntries(areas.map(a => [a, countByArea(a)]));
      await API.post(`/inspection/${requestId}/step/5/save`, {
        photos_per_area: counts,
        descriptions_accepted: true,
      });
      toast.success("Photo evidence captured");
      onAdvance(6);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not save");
    } finally { setSaving(false); }
  };

  const photosInArea = photos.filter(p => (p.area || "Other") === selectedArea);

  return (
    <div className="space-y-4" data-testid="step-5-photos">
      <div className="flex items-center gap-2">
        <Camera className="w-5 h-5 text-amber-600" />
        <h2 className="text-base font-bold text-zinc-900">Step 5 · Damage Photos ({MIN_PER_AREA} min per area)</h2>
      </div>

      {/* Area selector */}
      <Card><CardContent className="pt-4">
        <div className="flex gap-1.5 overflow-x-auto pb-1 -mx-1 px-1">
          {areas.map(a => {
            const cnt = countByArea(a);
            const met = cnt >= MIN_PER_AREA;
            const isSel = selectedArea === a;
            return (
              <button key={a} onClick={() => setSelectedArea(a)}
                      className={`flex-shrink-0 flex items-center gap-1.5 px-3 py-2 rounded-full border text-xs font-medium transition-all
                                  ${isSel ? "ring-2 ring-amber-300" : ""}
                                  ${met ? "bg-emerald-50 border-emerald-300 text-emerald-700" : "bg-white border-zinc-200 text-zinc-600"}`}
                      data-testid={`step-5-area-tab-${a.toLowerCase().replace(/ /g, "-")}`}>
                {met ? <Check className="w-3 h-3" /> : <AlertCircle className="w-3 h-3" />}
                {a}
                <span className="text-[10px] font-semibold ml-1">{cnt}/{MIN_PER_AREA}</span>
              </button>
            );
          })}
        </div>
      </CardContent></Card>

      <Card><CardContent className="pt-4">
        <div className="flex items-center justify-between mb-2">
          <p className="text-sm font-semibold text-zinc-900">
            {selectedArea} — {photosInArea.length} photos
          </p>
          {countByArea(selectedArea) >= MIN_PER_AREA
            ? <Badge className="bg-emerald-100 text-emerald-700">Minimum met</Badge>
            : <Badge className="bg-amber-100 text-amber-800">{MIN_PER_AREA - countByArea(selectedArea)} more required</Badge>}
        </div>
        <input ref={camRef} type="file" accept="image/*,.heic,.heif" capture="environment"
               className="hidden" onChange={upload} data-testid="step-5-camera-input" />
        <input ref={fileRef} type="file" accept="image/*,.heic,.heif"
               className="hidden" onChange={upload} data-testid="step-5-file-input" />
        <div className="grid grid-cols-2 gap-2">
          <Button onClick={() => openCamera(camRef)} disabled={uploading}
                  className="h-12" data-testid="step-5-take-photo">
            {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Camera className="w-4 h-4 mr-1" /> Take Photo</>}
          </Button>
          <Button onClick={() => openPhotoPicker(fileRef)} disabled={uploading}
                  variant="outline" className="h-12" data-testid="step-5-upload">
            <ImageIcon className="w-4 h-4 mr-1" /> Upload
          </Button>
        </div>

        <div className="mt-3 space-y-2">
          {photosInArea.map(p => (
            <div key={p.id} className="border border-zinc-200 rounded-lg p-2"
                 data-testid={`step-5-photo-${p.id}`}>
              <img src={`${process.env.REACT_APP_BACKEND_URL}/api/photos/${p.id}/image`}
                   alt="" className="w-full h-40 object-cover rounded" />
              {editingId === p.id ? (
                <>
                  <textarea value={editText} onChange={e => setEditText(e.target.value)}
                            className="w-full mt-2 p-2 text-xs border rounded h-20"
                            data-testid={`step-5-edit-text-${p.id}`} />
                  <div className="flex gap-2 mt-1">
                    <Button size="sm" variant="outline" className="flex-1" onClick={() => setEditingId(null)}>Cancel</Button>
                    <Button size="sm" className="flex-1 bg-zinc-900 text-white" onClick={() => saveEdit(p)}
                            data-testid={`step-5-save-desc-${p.id}`}>Save</Button>
                  </div>
                </>
              ) : (
                <>
                  <p className="text-xs text-zinc-700 mt-2 leading-relaxed">
                    {p.description || p.ai_description || <em className="text-zinc-400">No description yet</em>}
                  </p>
                  {!p.is_approved ? (
                    <div className="flex gap-2 mt-2">
                      <Button size="sm" variant="outline" className="flex-1" onClick={() => startEdit(p)}
                              data-testid={`step-5-edit-${p.id}`}>
                        <Edit2 className="w-3 h-3 mr-1" /> Edit
                      </Button>
                      <Button size="sm" className="flex-1 bg-emerald-600 text-white" onClick={() => accept(p)}
                              data-testid={`step-5-accept-${p.id}`}>
                        <Check className="w-3 h-3 mr-1" /> Accept
                      </Button>
                    </div>
                  ) : (
                    <Badge className="mt-2 bg-emerald-100 text-emerald-700"><Check className="w-3 h-3 mr-0.5" /> Accepted</Badge>
                  )}
                </>
              )}
            </div>
          ))}
        </div>
      </CardContent></Card>

      <Button onClick={continueStep} disabled={!canContinue || saving}
              className="w-full h-12 bg-zinc-900 hover:bg-zinc-800 text-white disabled:bg-zinc-200 disabled:text-zinc-400"
              data-testid="step-5-continue">
        {saving ? <Loader2 className="w-4 h-4 animate-spin" /> :
          canContinue ? "Continue to Scope of Work" :
          !allMin ? "Capture 5 photos in every area" : "Accept every description to continue"}
      </Button>
    </div>
  );
}
